import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import * as blacklistService from '@/supabase/blacklist/service/blacklistService';
import { BlacklistEntry } from '@/supabase/blacklist/types/blacklist';
import { logError } from '@/lib/sentry';

interface UseBlacklistProps {
  user: User | null;
}

export type BlacklistStatus = 'loading' | 'ready' | 'error';

export interface UseBlacklistResult {
  /** 거파 명단 (created_at desc). 비로그인/조회 실패/소유자 불일치 프레임 = 빈 배열 */
  blacklistEntries: BlacklistEntry[];
  /** 조회 상태. 'error'면 명단 미로드 상태 — 등록/해제는 'ready'에서만 동작 */
  blacklistStatus: BlacklistStatus;
  /** trim 닉네임 → entry. entries 변경 시에만 재생성 (memo 자식 계약) */
  blacklistByNickname: Map<string, BlacklistEntry>;
  /** 등록/재등록(사유 갱신). 성공 여부 반환. status 'ready' 전엔 false */
  registerBlacklist: (nickname: string, reason: string) => Promise<boolean>;
  /** 해제. 성공 여부 반환. status 'ready' 전엔 false */
  unregisterBlacklist: (nickname: string) => Promise<boolean>;
}

// state에 소유자를 동봉 — 렌더 시점에 "이 entries가 지금 유저 것인지"를 동기 판정 가능하게 한다.
interface BlacklistState {
  ownerUserId: string | null;
  entries: BlacklistEntry[];
  status: BlacklistStatus;
}

const EMPTY_ENTRIES: BlacklistEntry[] = [];
const INITIAL_STATE: BlacklistState = { ownerUserId: null, entries: EMPTY_ENTRIES, status: 'ready' };

/**
 * blacklist 도메인 훅 — Home이 1회 호출하는 공유 허브 (스펙 §4·§6).
 * 로그인당 조회 1회. 빈 명단 셀러 = SELECT 1회(빈 결과)가 비용의 전부 (스펙 §7).
 * user 객체가 아닌 user.id에 의존 — USER_UPDATED(메타데이터 갱신)로 인한
 * 재조회·참조 교체(memo barrier 붕괴)를 차단한다 (codexloop R1-6).
 * 계정 A→B(→A) 전환: generation 가드가 구 fetch/mutation 결과를 무효화하고(R2-2),
 * 소유자 불일치 프레임은 아래 동기 파생으로 빈/로딩 뷰를 반환한다(R2-1).
 */
export function useBlacklist({ user }: UseBlacklistProps): UseBlacklistResult {
  const userId = user?.id ?? null;
  const [state, setState] = useState<BlacklistState>(INITIAL_STATE);
  // 소유자 전환마다 단조 증가 — A→B→A 왕복에서 ownerRef 단순 비교가 놓치는
  // 구세대 fetch(페이지 혼입 포함)를 세대 번호로 무효화한다 (codexloop R2-2).
  const generationRef = useRef(0);

  useEffect(() => {
    generationRef.current += 1;
    const gen = generationRef.current;
    if (!userId) {
      setState(INITIAL_STATE);
      return;
    }
    setState({ ownerUserId: userId, entries: EMPTY_ENTRIES, status: 'loading' });
    blacklistService.getOwnBlacklist(userId)
      .then(entries => {
        if (generationRef.current !== gen) return;
        setState({ ownerUserId: userId, entries, status: 'ready' });
      })
      .catch(err => {
        if (generationRef.current !== gen) return;
        logError('[useBlacklist] 거파 명단 조회 실패:', err);
        setState({ ownerUserId: userId, entries: EMPTY_ENTRIES, status: 'error' });
      });
  }, [userId]);

  // 소유자 불일치 프레임(계정 전환 직후 effect 실행 전) = 동기적으로 빈/로딩 뷰 파생.
  // 이 파생이 stale entries 1프레임 노출과 stale 'ready' 게이트 통과를 동시에 차단한다
  // (useInventoryQuickAdd 선례, codexloop R2-1).
  const owned = state.ownerUserId === userId;
  const blacklistEntries = owned ? state.entries : EMPTY_ENTRIES;
  const blacklistStatus: BlacklistStatus = owned ? state.status : 'loading';

  const blacklistByNickname = useMemo(() => {
    const map = new Map<string, BlacklistEntry>();
    for (const entry of blacklistEntries) {
      map.set(entry.nickname.trim(), entry);
    }
    return map;
  }, [blacklistEntries]);

  const registerBlacklist = useCallback(async (nickname: string, reason: string): Promise<boolean> => {
    // 'ready' 게이트: 초기 로드/에러/소유자 불일치 프레임 중 mutation 차단
    // (useMemberPreference 선례, codexloop R1-3·R2-1)
    if (!userId || blacklistStatus !== 'ready') return false;
    const gen = generationRef.current;
    try {
      const saved = await blacklistService.registerEntry(userId, nickname, reason);
      if (generationRef.current === gen) {
        setState(prev => {
          if (prev.ownerUserId !== userId) return prev;
          // 재등록(동일 id) = 제자리 갱신 — created_at desc 순서 보존 (codexloop R1-5)
          const exists = prev.entries.some(e => e.id === saved.id);
          const entries = exists
            ? prev.entries.map(e => (e.id === saved.id ? saved : e))
            : [saved, ...prev.entries];
          return { ...prev, entries };
        });
      }
      return true;
    } catch (err) {
      logError('[useBlacklist] 거파 등록 실패:', err);
      return false;
    }
  }, [userId, blacklistStatus]);

  const unregisterBlacklist = useCallback(async (nickname: string): Promise<boolean> => {
    if (!userId || blacklistStatus !== 'ready') return false;
    const key = nickname.trim();
    const gen = generationRef.current;
    try {
      await blacklistService.unregisterEntry(userId, key);
      if (generationRef.current === gen) {
        setState(prev => {
          if (prev.ownerUserId !== userId) return prev;
          return { ...prev, entries: prev.entries.filter(e => e.nickname.trim() !== key) };
        });
      }
      return true;
    } catch (err) {
      logError('[useBlacklist] 거파 해제 실패:', err);
      return false;
    }
  }, [userId, blacklistStatus]);

  return { blacklistEntries, blacklistStatus, blacklistByNickname, registerBlacklist, unregisterBlacklist };
}
